import { useSnackbars } from '@customerio/pluma-components/react';
import { authStore } from '@/services/auth/store';
import { useStore } from '@nanostores/react';
import { hydraUiApi } from '@/services/api';
import { sentryCaptureException } from '@/utils/sentry';
import type { FeedbackFormValues } from '../types';

type UseSubmitFeedbackOptions = {
  /**
   * Whether a success snackbar is raised. Callers that already confirm inline
   * (e.g. a modal that swaps to its own thank-you panel) pass `false`.
   */
  withSuccessSnackbar?: boolean;
};

export function useSubmitFeedback({ withSuccessSnackbar = true }: UseSubmitFeedbackOptions = {}) {
  const auth = useStore(authStore);
  const snackbars = useSnackbars();

  const submitFeedback = async (values: FeedbackFormValues): Promise<void> => {
    if (auth.account == null || auth.user == null) {
      throw new Error('Cannot submit feedback without auth data');
    }

    const { account } = auth;

    try {
      await hydraUiApi.support.feedback({
        params: { path: { account_id: String(account.id) } },
        body: {
          replyTo: values.replyTo,
          source: values.source ?? '',
          browser: navigator.userAgent,
          url: window.location.href,
          windowDimensions: `${window.innerWidth}x${window.innerHeight}`,
          criticism: values.criticism ?? '',
          praise: values.praise ?? '',
        },
      });

      if (withSuccessSnackbar) {
        snackbars.create({
          message: 'Thank you for your feedback.',
          variant: 'success',
        });
      }
    } catch (error) {
      sentryCaptureException(error);
      snackbars.create({
        message: 'Unable to submit feedback. Try again.',
        variant: 'critical',
      });
      throw error;
    }
  };

  return submitFeedback;
}
